import React, { useEffect } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { useLocation } from 'react-router';
import DataContainer from 'Components/UI/DataContainer';
import useCollection from 'Hooks/useCollection';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import useUserContext from 'Context/UserContext';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import usePrevious from 'Hooks/usePrevious';
import { QueryVal, removeQuery } from 'Helpers/Query';
import Header from './ActionItemsHeader';
import List from './ActionItemList';
import ActionItemsListDialog from './ActionItemsListDialog';

const useStyles = makeStyles(theme => ({
  agentTitle: {
    color: theme.palette.facet.primaryBlue,
    padding: theme.spacing(2, 2, 1, 2),
  },
}));

const ACTION_ITEM_RENDER_LIMIT = 5;

export default function ActionItems({
  readOnly,
  showDivider,
  displayAdvisorTasks,
  HeaderProps,
}) {
  const classes = useStyles();
  const location = useLocation();
  const prevLocation = usePrevious(location);
  const { householdResource } = useSimpleHousehold();
  const { isAgent } = useUserContext();
  const {
    collection: clientTodoCollection,
    setCollection: setClientTodoCollection,
  } = useCollection();
  const {
    collection: agentTodoCollection,
    setCollection: setAgentTodoCollection,
  } = useCollection();
  const [loading, setLoading] = React.useState(true);


  const getClientActionItems = () => {
    const params = {
      hidden: 'false',
      notWorkflow: 'true',
      assignedTo: 'household',
      sort: 'completedAt',
    };

    if (QueryVal('showHidden')) {
      params.hidden = 'true';
    }

    return householdResource
      .relationship('actionItems')
      .link('related')
      .Get({ params })
      .then((col) => {
        // If we get a redirect we will create a collection w/o a self link - See FW-2582
        if (col && col.link('self')) {
          setClientTodoCollection(col);
          return col.fetchAllPages();
        }
        return null;
      })
      .then((fullCol) => {
        if (fullCol) {
          setClientTodoCollection(fullCol);
        }
      });
  };
  const getAgentActionItems = () => {
    const params = {
      hidden: 'false',
      notWorkflow: 'true',
      assignedTo: 'facet',
      sort: 'completedAt',
    };

    if (QueryVal('showHidden')) {
      params.hidden = 'true';
    }

    return householdResource
      .relationship('actionItems')
      .link('related')
      .Get({ params })
      .then((col) => {
      // If we get a redirect we will create a collection w/o a self link - See FW-2582
        if (col && col.link('self')) {
          setAgentTodoCollection(col);
          return col.fetchAllPages();
        }
        return null;
      })
      .then((fullCol) => {
        if (fullCol) {
          setAgentTodoCollection(fullCol);
        }
      });
  };

  const replaceClientActionItem = (resource) => {
    clientTodoCollection.resources().updateItem(resource);
    setClientTodoCollection(clientTodoCollection);
  };
  const replaceAgentActionItem = (resource) => {
    agentTodoCollection.resources().updateItem(resource);
    setAgentTodoCollection(agentTodoCollection);
  };

  const refreshActionItems = () => {
    setLoading(true);
    try {
      if (clientTodoCollection) {
        clientTodoCollection.refresh()
          .then(next => setClientTodoCollection(next));
      }
      if (agentTodoCollection) {
        agentTodoCollection.refresh().then(next => setAgentTodoCollection(next));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (prevLocation
      && prevLocation.search !== location.search
      && prevLocation.search.includes('modal')
      && !location.search.includes('modal')) {
      refreshActionItems();
    }
  }, [location]);

  React.useEffect(() => {
    setLoading(true);
    try {
      if (householdResource) {
        getClientActionItems().catch((err) => {
          displayErrorMessage(err, err.errors);
        });
        getAgentActionItems().catch((err) => {
          displayErrorMessage(err, err.errors);
        });
      }
    } finally {
      setLoading(false);
    }
  }, [householdResource]);

  React.useEffect(() => {
    if (QueryVal('refresh') === 'action-items') {
      removeQuery('refresh');
      setLoading(true);
      Promise.all([getClientActionItems(), getAgentActionItems()]).finally(() => {
        setLoading(false);
      });
    } else if (QueryVal('refresh') === 'add-action-item') {
      setLoading(true);
      getClientActionItems().finally(() => {
        setLoading(false);
      });
    }
  }, [location.search]);

  if (clientTodoCollection === null) return null;
  if (isAgent && agentTodoCollection === null) return null;

  return (
    <>
      <DataContainer loading={loading}>
        {isAgent && displayAdvisorTasks ? (
          <Box>
            <Header
              count={
                clientTodoCollection.resources().length()
                + agentTodoCollection.resources().length()
              }
              loading={loading}
              readOnly={readOnly}
              {...HeaderProps}
            />
            <Typography variant="h3" className={classes.agentTitle}>
            Client Action Items:
            </Typography>
            <List
              renderLimit={ACTION_ITEM_RENDER_LIMIT}
              actionItemCollection={clientTodoCollection}
              refreshActionItems={refreshActionItems}
              replaceActionItem={replaceClientActionItem}
              showDivider
              agent
            />
            <Typography variant="h3" className={classes.agentTitle}>
            Facet Action Items:
            </Typography>
            <List
              renderLimit={ACTION_ITEM_RENDER_LIMIT}
              actionItemCollection={agentTodoCollection}
              refreshActionItems={refreshActionItems}
              replaceActionItem={replaceAgentActionItem}
              readOnly={readOnly}
              shoDivider={showDivider}
            />
          </Box>
        ) : (
          <Box>
            <Header
              count={clientTodoCollection.resources().length()}
              {...HeaderProps}
            />
            <List
              renderLimit={ACTION_ITEM_RENDER_LIMIT}
              loading={loading}
              actionItemCollection={clientTodoCollection}
              refreshActionItems={refreshActionItems}
              replaceActionItem={replaceClientActionItem}
              showDivider={showDivider}
              readOnly={readOnly}
            />
          </Box>
        )}
      </DataContainer>
      <ActionItemsListDialog
        collection={clientTodoCollection}
        replaceActionItem={replaceClientActionItem}
        refreshActionItems={refreshActionItems}
        loading={loading}
      />
    </>
  );
}

ActionItems.defaultProps = {
  readOnly: false,
  showDivider: false,
  displayAdvisorTasks: true,
};

ActionItems.propTypes = {
  readOnly: PropTypes.bool.isRequired,
  showDivider: PropTypes.bool.isRequired,
  displayAdvisorTasks: PropTypes.bool,
};
